// ErpRouteGuard — auth gate for the School ERP portal (/erp/*).
//
// Two ways in:
//   1. ERP staff who logged in at /erp/login → an `erp_auth_token` cookie (pa-token).
//   2. CRM users entering the ERP → existing CRM session (crm_auth_token).
//
// If neither is present, redirect to the dedicated ERP login. We check token
// presence only; an invalid/expired token surfaces as a 401 on the first API
// call, which the portal handles by sending the user back here.

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { authService } from './authService';
import { erpPath } from '../erp-host';

interface ErpRouteGuardProps {
  children: React.ReactNode;
}

const ErpRouteGuard: React.FC<ErpRouteGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [checked, setChecked] = useState(false);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const hasErpToken = !!Cookies.get('erp_auth_token');
    const hasCrmAuth = authService.isAuthenticated();

    if (hasErpToken || hasCrmAuth) {
      setAllowed(true);
    } else {
      const redirect = encodeURIComponent(location.pathname + location.search);
      navigate(`${erpPath('login')}?redirect=${redirect}`, { replace: true });
    }
    setChecked(true);
  }, [navigate, location.pathname, location.search]);

  if (!checked || !allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        <span className="ml-2 text-gray-600">Loading ERP…</span>
      </div>
    );
  }

  return <>{children}</>;
};

export default ErpRouteGuard;
